import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import {trans} from '@frontend/helpers'
import {
    mdiCheckAll,
    mdiCheckboxBlankOutline,
    mdiCheckboxMarked,
} from '@mdi/js'
import {
    useEffect,
    useState,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Card} from 'swayok-react-mdb-ui-kit/components/Card/Card'
import {CardBody} from 'swayok-react-mdb-ui-kit/components/Card/CardBody'
import {CardHeader} from 'swayok-react-mdb-ui-kit/components/Card/CardHeader'
import {CardTitle} from 'swayok-react-mdb-ui-kit/components/Card/CardTitle'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {ButtonsSwitchInput} from 'swayok-react-mdb-ui-kit/components/Input/ButtonsSwitchInput'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ChatsApi} from '../ChatsApi'
import type {
    ChatDetailsData,
    ChatsFilter,
} from '../ChatsModuleTypes'
import {ChatsListItem} from './ChatsListItem'

interface Props {
    miniChat?: boolean
    filter: ChatsFilter
    chats: ChatDetailsData[] | null
    isAllChatsLoaded: boolean
    // Перейти к сообщениям чата.
    setChat: (chat: ChatDetailsData | null) => void
    // Сохранить загруженные чаты.
    setChats: (filter: ChatsFilter, chats: ChatDetailsData[], isAllChatsLoaded: boolean) => void// Удаление чата.
    // Удалить чаты.
    onDelete: (chats: number[]) => void
    // Прочитать все сообщения для списка чатов.
    onReadAllMessages: (chats: number[]) => void
    // Показать окно добавления отзыва о продавце.
    onAddReview: (chat: ChatDetailsData) => void
    // Состояние выполнения действия с чатами.
    isProcessing?: boolean
}

const limitPerPage: number = 20

// Список чатов.
export function ChatsList(props: Props) {

    const {
        miniChat = false,
        filter: filterFromProps,
        chats,
        isAllChatsLoaded = false,
        setChat,
        setChats,
        onDelete,
        onReadAllMessages,
        onAddReview,
        isProcessing = false,
    } = props

    // Список выбранных чатов.
    const [
        selectedItems,
        setSelectedItems,
    ] = useState<number[]>([])

    // Фильтр сообщений.
    const [
        filter,
        setFilter,
    ] = useState<ChatsFilter>(filterFromProps)

    // Состояния загрузки списка чатов.
    const [
        isLoading,
        setIsLoading,
    ] = useState<boolean>(chats === null)
    const [
        isLoadingError,
        setIsLoadingError,
    ] = useState<boolean>(false)
    const [
        isLoadingMoreChats,
        setIsLoadingMoreChats,
    ] = useState<boolean>(false)

    // Загрузка списка чатов.
    const loadChats = (nextPage: boolean = false, newFilter?: ChatsFilter) => {
        if (!chats) {
            nextPage = false
        }
        setIsLoading(!nextPage)
        setIsLoadingMoreChats(nextPage)
        setIsLoadingError(false)
        if (newFilter === undefined) {
            newFilter = filter
        }
        ChatsApi.getChatsList(
            newFilter,
            limitPerPage,
            nextPage ? chats?.length ?? 0 : 0
        )
            .then(data => {
                setChats(
                    filter,
                    nextPage ? (chats ?? []).concat(data.records) : data.records,
                    data.records.length < limitPerPage
                )
            })
            .catch((error: ApiError) => {
                if (!nextPage) {
                    setIsLoadingError(true)
                } else {
                    handleErrorResponse(error)
                }
            })
            .finally(() => {
                setIsLoading(false)
                setIsLoadingMoreChats(false)
            })
    }

    // Загружаем первоначальный список чатов.
    useEffect(() => {
        if (chats === null) {
            loadChats(false)
        }
    }, [])

    // Выделить или снять выделение с объявления.
    const toggleSelection = (id: number): void => {
        setSelectedItems((ids: number[]): number[] => {
            const newIds: number[] = ids.slice()
            if (newIds.includes(id)) {
                newIds.splice(newIds.indexOf(id), 1)
            } else {
                newIds.push(id)
            }
            return newIds
        })
    }

    const isAllSelected: boolean = !!chats?.length && chats.every(chat => selectedItems.includes(chat.id))

    // Выделить все загруженные чаты или снять выделение со всех.
    const toggleAllSelection = (): void => {
        setSelectedItems(isAllSelected ? [] : (chats ?? []).map(chat => chat.id))
    }

    const content = (
        <AsyncDataLoading
            loading={isLoading}
            error={isLoadingError}
            onReload={loadChats}
            render={() => {
                if (chats?.length === 0) {
                    return (
                        <div className="py-6 px-5 text-center fs-5">
                            {trans().cabinet.chats.no_chats}
                        </div>
                    )
                }
                return (
                    <div className="p-2">
                        {chats?.map((chat: ChatDetailsData, index: number) => (
                            <ChatsListItem
                                key={'chat' + chat.id}
                                miniChat={miniChat}
                                chat={chat}
                                selected={selectedItems.includes(chat.id)}
                                isLast={index + 1 === chats?.length}
                                disabled={isProcessing}
                                onSelect={toggleSelection}
                                onChatOpen={setChat}
                                onDelete={chat => onDelete([chat.id])}
                                onReadAllMessages={chat => onReadAllMessages([chat.id])}
                                onAddReview={onAddReview}
                            />
                        ))}
                        <Collapse
                            show={!isAllChatsLoaded}
                            showImmediately
                        >
                            <div className="flex-center pt-3">
                                <Button
                                    color="gray"
                                    onClick={() => loadChats(true)}
                                    disabled={isLoadingMoreChats}
                                >
                                    {trans().common.load_more_records}
                                </Button>
                            </div>
                        </Collapse>
                    </div>
                )
            }}
        />
    )

    if (miniChat) {
        return (
            <div className="cabinet-chats">
                {content}
            </div>
        )
    }

    return (
        <Card className="cabinet-chats">
            <CardHeader className="pt-4 d-flex flex-column align-items-stretch position-relative flex-sm-row align-items-sm-center justify-content-start">
                <CardTitle className="flex-1 text-center text-sm-start">
                    {trans().cabinet.chats.header}
                </CardTitle>
                <ButtonsSwitchInput<ChatsFilter>
                    className="m-0 mt-3 mt-sm-0 flex-shrink-0 flex-center"
                    value={filter}
                    options={[
                        {
                            label: trans().cabinet.chats.filter.all,
                            value: null,
                        },
                        {
                            label: trans().cabinet.chats.filter.not_viewed,
                            value: 'not_viewed',
                        },
                    ]}
                    onChange={(value: ChatsFilter) => {
                        setFilter(value)
                        loadChats(false, value)
                    }}
                />
            </CardHeader>
            <CardBody className="p-0">
                {!!chats?.length && (
                    <div className="px-2 pt-2">
                        <Button
                            color="none"
                            className="full-width d-flex align-items-center gap-2 p-3 border-0 shadow-none rounded-7 chat-surface text-start"
                            disabled={isProcessing}
                            onClick={toggleAllSelection}
                        >
                            <Icon
                                path={isAllSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
                                color={isAllSelected ? 'green' : 'muted'}
                            />
                            {trans().cabinet.chats.select_all}
                        </Button>
                    </div>
                )}
                {content}
                {selectedItems.length > 0 && (
                    <div className="cabinet-chats-bulk-actions position-fixed d-flex align-items-center gap-3 p-2 ps-3 rounded-7 text-white shadow-3">
                        <span className="d-flex align-items-center gap-1 text-nowrap">
                            <Icon
                                path={mdiCheckAll}
                                color="white"
                            />
                            {selectedItems.length}
                        </span>
                        <Button
                            color="blue"
                            small
                            className="text-nowrap"
                            disabled={isProcessing}
                            onClick={() => {
                                onReadAllMessages(selectedItems)
                                setSelectedItems([])
                            }}
                        >
                            {trans().cabinet.chats.action.mark_selected_as_read}
                        </Button>
                        <Button
                            color="white"
                            outline
                            small
                            className="text-white border"
                            disabled={isProcessing}
                            onClick={() => {
                                if (confirm(trans().cabinet.chats.action.delete_selected_chats_confirmation)) {
                                    onDelete(selectedItems)
                                    setSelectedItems([])
                                }
                            }}
                        >
                            {trans().cabinet.chats.action.delete_selected}
                        </Button>
                    </div>
                )}
            </CardBody>
        </Card>
    )
}
